import React, { useEffect, useRef, useState, ReactNode } from 'react';
import { text } from '../text';
import { theme } from '../constants';

// Layout "tab" untuk halaman TaskDetails (alternatif layout scroll, dipilih user di Profil).
// Komponen ini HANYA mengatur susunan — semua state, handler, modal & isi section tetap milik
// TaskDetails.tsx dan dikirim lewat render function, supaya kedua layout memakai logika yang
// sama persis. Status card + tab bar menempel di atas, tombol aksi (fixed di TaskDetails)
// tetap di bawah — aksi tidak perlu pindah tab.
export type TaskDetailTabKey = 'detail' | 'documents' | 'group' | 'checker' | 'report' | 'testing';

export interface TaskDetailTab {
  key: TaskDetailTabKey;
  label: string;
  render: () => ReactNode;
  badge?: number;       // angka kecil di label tab (mis. jumlah report)
  dot?: boolean;        // titik merah: ada yang perlu tindakan/perhatian user ini
  isEmpty?: boolean;    // tampilkan emptyText alih-alih render()
  emptyText?: string;
}

interface Props {
  taskId: number | string;
  topOffset: number;                 // margin atas konten (sama dgn layout scroll)
  stickyTop: number;                 // posisi menempel = tepat di bawah header / banner offline
  statusCard: ReactNode;
  tabs: TaskDetailTab[];
  defaultTab: TaskDetailTabKey;
  // Permintaan pindah tab dari luar (mis. dibuka dari notifikasi komentar). nonce berubah
  // = permintaan baru, walau tab-nya sama dengan permintaan sebelumnya.
  tabRequest?: { tab: TaskDetailTabKey; nonce: number } | null;
  onActiveTabChange?: (tab: TaskDetailTabKey) => void;
  dotLabel?: string;
}

const storageKey = (taskId: number | string) => `taskDetailTab:${taskId}`;

const readStoredTab = (taskId: number | string): TaskDetailTabKey | null => {
  try {
    return (sessionStorage.getItem(storageKey(taskId)) as TaskDetailTabKey) || null;
  } catch {
    return null;
  }
};

export const TaskDetailsTabLayout: React.FC<Props> = ({
  taskId, topOffset, stickyTop, statusCard, tabs, defaultTab, tabRequest, onActiveTabChange, dotLabel,
}) => {
  const available = tabs.map((tb) => tb.key);
  const pickValid = (key: TaskDetailTabKey | null | undefined) =>
    key && available.includes(key) ? key : null;

  // Tab terakhir per task diingat selama sesi browser (kembali dari form rekapan, dll.)
  const [activeTab, setActiveTab] = useState<TaskDetailTabKey>(
    () => pickValid(readStoredTab(taskId)) || pickValid(defaultTab) || available[0]
  );

  // Tab aktif hilang (mis. task tidak punya checker lagi) → kembali ke tab pertama
  useEffect(() => {
    if (!available.includes(activeTab)) setActiveTab(available[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available.join(',')]);

  useEffect(() => {
    const requested = pickValid(tabRequest?.tab);
    if (requested) setActiveTab(requested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabRequest?.nonce]);

  useEffect(() => {
    try { sessionStorage.setItem(storageKey(taskId), activeTab); } catch { /* noop */ }
    onActiveTabChange?.(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, taskId]);

  const handleSelect = (key: TaskDetailTabKey) => {
    if (key === activeTab) return;
    setActiveTab(key);
    // Kontainer scroll halaman bisa #screen atau window tergantung tinggi konten — reset keduanya
    document.getElementById('screen')?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  };

  const current = tabs.find((tb) => tb.key === activeTab) || tabs[0];

  // Tab bar bisa di-scroll ke samping kalau tab-nya banyak — tab aktif selalu digeser ke area terlihat
  // Dihitung langsung di kontainer tab (bukan scrollIntoView) supaya halaman tidak ikut bergeser.
  // Pertama tampil (mis. kembali ke task dgn tab terakhir = Testing) → instan; pindah tab → smooth.
  // Diulang saat daftar tab berubah, krn tab spt Testing/Grup baru muncul setelah data task dimuat.
  const tabListRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const firstTabScroll = useRef(true);
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const list = tabListRef.current;
      const btn = tabRefs.current[activeTab];
      if (!list || !btn) return;
      const left = btn.offsetLeft;
      const right = left + btn.offsetWidth;
      let target = list.scrollLeft;
      if (left < list.scrollLeft) target = left - 12;
      else if (right > list.scrollLeft + list.clientWidth) target = right - list.clientWidth + 12;
      list.scrollTo({ left: Math.max(0, target), behavior: firstTabScroll.current ? 'auto' : 'smooth' });
      firstTabScroll.current = false;
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, available.join(',')]);

  return (
    <div className='container' style={{ marginTop: stickyTop, paddingBottom: 100 }}>
      <div style={{
        // Di bawah Header (zIndex 6) — dropdown kebab menu ada di dalam stacking context header,
        // jadi area sticky harus lebih rendah supaya tidak menutupi menunya.
        position: 'sticky', top: stickyTop, zIndex: 5,
        backgroundColor: theme.colors.white, paddingTop: Math.max(topOffset - stickyTop, 0),
        marginLeft: -20, marginRight: -20, paddingLeft: 20, paddingRight: 20,
      }}>
        {statusCard}
        {/* Tab tidak pernah menyusut di bawah lebar judulnya (tanpa ellipsis); kalau tidak muat,
            baris tab di-scroll ke samping. Tab sedikit → tetap mengisi lebar penuh (flex-grow). */}
        <div ref={tabListRef} role='tablist' className='task-detail-tabs' style={{
          display: 'flex', borderBottom: '1px solid #E5E7EB', position: 'relative',
          overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
        } as React.CSSProperties}>
          <style>{`.task-detail-tabs::-webkit-scrollbar { display: none; }`}</style>
          {tabs.map((tb) => {
            const isActive = tb.key === activeTab;
            return (
              <button
                key={tb.key}
                ref={(el) => { tabRefs.current[tb.key] = el; }}
                role='tab'
                aria-selected={isActive}
                onClick={() => handleSelect(tb.key)}
                style={{
                  flex: '1 0 auto', background: 'none', border: 'none', cursor: 'pointer',
                  padding: '10px 12px', position: 'relative',
                  borderBottom: `2px solid ${isActive ? theme.colors.mainColor : 'transparent'}`,
                  marginBottom: -1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                <text.T14 style={{
                  fontSize: 13, fontWeight: isActive ? '700' : '500',
                  color: isActive ? theme.colors.mainColor : '#6B7280',
                  whiteSpace: 'nowrap',
                }}>
                  {tb.label}
                </text.T14>
                {!!tb.badge && tb.badge > 0 && (
                  <span style={{
                    minWidth: 16, height: 16, borderRadius: 8, padding: '0 4px',
                    backgroundColor: isActive ? theme.colors.mainColor : '#E5E7EB',
                    color: isActive ? '#FFFFFF' : '#374151',
                    fontSize: 10, fontWeight: 700, lineHeight: '16px', textAlign: 'center',
                  }}>
                    {tb.badge}
                  </span>
                )}
                {tb.dot && (
                  <span
                    title={dotLabel}
                    aria-label={dotLabel}
                    style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#EF4444', flexShrink: 0 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div role='tabpanel' style={{ paddingTop: 16 }}>
        {current?.isEmpty
          ? <text.T14 style={{ color: '#9CA3AF', textAlign: 'center', marginTop: 40 }}>{current.emptyText}</text.T14>
          : current?.render()}
      </div>
    </div>
  );
};
